import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Refresh,
  People,
  AccountBalance,
  ShowChart,
  ListAlt,
  Rule,
  TrendingUp,
  SmartToy,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { AdminService, UsageStats, UserUsageStats } from '../services/adminService';

const AdminUsageDashboard: React.FC = () => {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [userStats, setUserStats] = useState<UserUsageStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageStats, perUserStats] = await Promise.all([
        AdminService.getUsageStats(),
        AdminService.getPerUserStats(),
      ]);
      setStats(usageStats);
      setUserStats(perUserStats);
    } catch (err: any) {
      console.error('Error loading usage stats:', err);
      setError(err.message || 'Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading && !stats) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Usage Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {stats?.lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {format(stats.lastUpdated, 'MMM dd, yyyy h:mm a')}
            </Typography>
          )}
          <Tooltip title="Refresh stats">
            <IconButton onClick={loadStats} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light' }}>
          <Typography color="error.contrastText">{error}</Typography>
        </Paper>
      )}

      {stats && (
        <>
          {/* Key Metrics Grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Unique Stocks - Important for API monitoring */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUp sx={{ mr: 1 }} />
                    <Typography variant="h6">Tracked Stocks</Typography>
                  </Box>
                  <Typography variant="h3">{stats.watchlist.uniqueSymbols}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Unique symbols being tracked globally
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Users */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <People sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Users</Typography>
                  </Box>
                  <Typography variant="h3">{stats.users.total}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`${stats.users.activeLastMonth} active`}
                      size="small"
                      color="success"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${stats.users.newLastMonth} new`}
                      size="small"
                      color="info"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    In the last 30 days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Portfolios */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Portfolios</Typography>
                  </Box>
                  <Typography variant="h3">{stats.portfolios.total}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`${stats.portfolios.real} real`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${stats.portfolios.virtual} virtual`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <SmartToy sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      {stats.portfolios.autopilotEnabled} with autopilot enabled
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Positions */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ShowChart sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Positions</Typography>
                  </Box>
                  <Typography variant="h3">{stats.positions.total}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`${stats.positions.open} open`}
                      size="small"
                      color="success"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${stats.positions.pending} pending`}
                      size="small"
                      color="warning"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${stats.positions.closed} closed`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Watchlist */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ListAlt sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Watchlist</Typography>
                  </Box>
                  <Typography variant="h3">{stats.watchlist.totalEntries}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`${stats.watchlist.activeEntries} active`}
                      size="small"
                      color="success"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${stats.watchlist.inactiveEntries} inactive`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Avg {stats.watchlist.avgPerUser} per user
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Trading Rules */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Rule sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Trading Rules</Typography>
                  </Box>
                  <Typography variant="h3">{stats.tradingRules.globalRules + stats.tradingRules.customRules}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`${stats.tradingRules.globalRules} global`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${stats.tradingRules.customRules} custom`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {stats.tradingRules.usersWithCustomRules} users with custom rules
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Per-User Usage Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Usage by User
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Individual user activity and resource usage (sorted by last active)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell align="center">Portfolios</TableCell>
                    <TableCell align="center">Autopilot</TableCell>
                    <TableCell align="center">Watchlist</TableCell>
                    <TableCell align="center">Open Pos.</TableCell>
                    <TableCell align="center">Rules</TableCell>
                    <TableCell align="right">Last Active</TableCell>
                    <TableCell align="right">Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userStats.map((user) => (
                    <TableRow key={user.userId} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.displayName || user.email.split('@')[0]}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={`${user.portfolios.real} real, ${user.portfolios.virtual} virtual`}>
                          <Typography variant="body2">{user.portfolios.total}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color={user.portfolios.autopilotEnabled > 0 ? 'warning.main' : 'text.secondary'}>
                          {user.portfolios.autopilotEnabled}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{user.watchlistSize}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color={user.openPositions > 0 ? 'success.main' : 'text.secondary'}>
                          {user.openPositions}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{user.customRules}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {user.lastActive ? (
                          <Tooltip title={format(user.lastActive, 'MMM dd, yyyy h:mm a')}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(user.lastActive, { addSuffix: true })}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {user.createdAt ? (
                          <Typography variant="caption" color="text.secondary">
                            {format(user.createdAt, 'MMM dd, yyyy')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {userStats.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">No users found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Popular Symbols Table */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Most Popular Symbols
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Stocks tracked by the most users (affects API rate limits)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Users Tracking</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.popularSymbols.map((item, index) => (
                    <TableRow key={item.symbol}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={index < 3 ? 'bold' : 'normal'}
                          color={index < 3 ? 'primary.main' : 'text.primary'}
                        >
                          #{index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={item.userCount}
                          size="small"
                          color={item.userCount > 2 ? 'primary' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {stats.popularSymbols.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography color="text.secondary">No tracked symbols found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AdminUsageDashboard;
